

import React from 'react'

import { Box, Typography, Stack } from '@mui/material'

import { ScanItem } from '../ScanItem'
import { NfcIcon } from 'lucide-react'

import { EmptyState } from 'components'
interface ScanListProps {
  scans: any[]
  onItemClick?: (id: string) => void
  isFirstPage: boolean
  activeId?: string
  /**
   * Shown when the list is empty. Supplied by the caller, matching the Analytics map's
   * `ScanList` — VRG-491.
   *
   * ⚠️ The defaults are a FALLBACK, not the normal path, and the reason this prop exists
   * is that the hardcoded copy they replace was often false. "This certificate has no
   * scans yet" was rendered whenever the list was empty, including when a filter or a
   * search had emptied a view of scans that plainly existed, and including when every
   * scan was present but unplottable because its visitor declined the location prompt.
   * Only the caller can tell those three apart.
   */
  emptyTitle?: string
  emptyText?: string
}

export const ScanList = (props: ScanListProps) => {
  const {
    scans,
    onItemClick,
    isFirstPage,
    activeId,
    emptyTitle = 'This certificate has no scans yet',
    emptyText = 'Perform scans to see geolocation analytics',
  } = props

  const isEmpty = scans.length === 0

  return (
    <Stack sx={{ height: '100%' }}>
      {isEmpty ? (
        // Fill the panel and center the empty state vertically (was pushed down with a
        // fixed pt, which left it top-heavy).
        <Stack sx={{ height: '100%' }} alignItems="center" justifyContent="center">
           <EmptyState
            title={emptyTitle}
            text={emptyText}
            icon={<NfcIcon strokeWidth={1} size={32} />}
            />
        </Stack>
      ) : (
        // No top padding: the first row (incl. its selected brand-tint highlight) fills
        // flush to the top edge — a top gap read as a stray white strip above a selected
        // first row. Bottom padding keeps breathing room for the last row.
        <Stack sx={{ pb: 2 }}>
          {scans.map((scan, index) => (
            <ScanItem
              key={index}
              scan={scan}
              onClick={onItemClick}
              isActive={activeId != null && String(scan.id) === String(activeId)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  )
}
